<script setup lang="ts">
import { IonApp, IonRouterOutlet } from '@ionic/vue';
import { provide, onMounted, computed } from 'vue';
import { isPlatform } from '@ionic/vue';
import { useSimpleSessionManager } from '@/composables/useSimpleSessionManager';
import { useTabVisibility } from '@/composables/useTabVisibility';
import { useStore } from '@/stores/main';
import { useRoute } from 'vue-router';
import FullscreenToggle from '@/components/FullscreenToggle.vue';
import InactivityModal from '@/components/InactivityModal.vue';
import ConnectionStatus from '@/components/ConnectionStatus.vue';
import { useAppConnection } from '@/composables/useAppConnection';
import { useRouter } from 'vue-router';

// Provide FMS type
provide('fms-type', import.meta.env.APP_FMS_TYPE);

// Detect iOS and provide to components
const isIOS = isPlatform('ios');
provide('isIOS', isIOS);

// Initialize PRODUCTION session management (4 hours)
const { startSessionTimer, extendSession, resetSession } = useSimpleSessionManager();

// Initialize tab visibility tracking for inactivity detection
const { showInactivityModal, closeInactivityModal } = useTabVisibility();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { status: connectionStatus, retry: retryConnection } = useAppConnection();

async function signInAgain() {
  await store.logout(true);
  router.replace('/login');
}

// Only show inactivity modal when user is logged in
const shouldShowInactivityModal = computed(() => {
  return store.loggedIn && showInactivityModal.value && connectionStatus.value === 'online';
});

const routerOutletKey = computed(() => (
  route.path.startsWith('/plan/') ? route.fullPath : 'app-router-outlet'
));

onMounted(() => {
  // Apply iOS-specific styling
  if (isIOS) {
    document.documentElement.classList.add('ios-device');
  }
  
  // Session management is handled automatically by the composable
});
</script>

<template>
  <ion-app>
    <ion-router-outlet :key="routerOutletKey" />
    <FullscreenToggle />
    <ConnectionStatus v-if="store.loggedIn" :status="connectionStatus" @retry="retryConnection" @login="signInAgain" />
    <InactivityModal 
      :is-open="shouldShowInactivityModal" 
      @close="closeInactivityModal" 
    />
  </ion-app>
</template>

<style>
/* Establish safe area variables */
:root {
  --ion-safe-area-top: env(safe-area-inset-top);
  --ion-safe-area-bottom: env(safe-area-inset-bottom);
  --ion-safe-area-left: env(safe-area-inset-left);
  --ion-safe-area-right: env(safe-area-inset-right);
}

/* Hide third-party/browser-extension screenshot overlay if injected. */
.rb-toast-message {
  display: none !important;
}

/* iOS-specific adjustments */
.ios-device ion-tab-bar {
  padding-bottom: var(--ion-safe-area-bottom);
  height: auto;
}

.ios-device ion-content {
  --padding-bottom: var(--ion-safe-area-bottom);
}

/* For your questions component */
.ios-device .questions-container {
  height: calc(100% - var(--ion-safe-area-bottom) - var(--navigation-area-height));
}

/* Fullscreen mode styles */
:fullscreen {
  background-color: #f9fcf2;
}

:-webkit-full-screen {
  background-color: #f9fcf2;
}

:-moz-full-screen {
  background-color: #f9fcf2;
}

:-ms-fullscreen {
  background-color: #f9fcf2;
}

/* Prevent browser UI from showing in fullscreen */
:fullscreen ion-app {
  height: 100vh;
  width: 100vw;
}

:-webkit-full-screen ion-app {
  height: 100vh;
  width: 100vw;
}

:-moz-full-screen ion-app {
  height: 100vh;
  width: 100vw;
}

:-ms-fullscreen ion-app {
  height: 100vh;
  width: 100vw;
}

/* Fullscreen toast styling */
.fullscreen-toast {
  --background: rgba(0, 0, 0, 0.8);
  --color: white;
  --border-radius: 8px;
  font-weight: 500;
}
</style>
